import { User } from '../hooks/useAuthOrAnon';
import Auth, { Google } from './auth';
import Streaks from './streaks';
import DevToolbar from './dev-toolbar';

// Templates
import appShell from './template/app-shell.html';
import footer from './template/footer.html';
import navBar from './template/navbar.html';
import accountMessage from './template/account-message.html';

import getCurrentStreak from '../utils/getCurrenStreak';
import getLast7Days from '../utils/getLast7Days';

function formatLevel(level: string): string {
  return level
    .split(':')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export interface HandleAuthAndSetupUIProps {
  gameKey: string;
  gameTitle: string;
  level: string;
  svgSrc: string;
  riveSrc: string;
  howToPlayURL: string;
  primaryColor: string;
  svgLogoSrc: string;
  showBlogLink?: boolean;
  localStorageKeys?: string[];
  devToolbarEnabled?: boolean;
}

async function renderProfileDropdown(
  user: User,
  gameTitle: string,
  streakLogs: string[],
  gameKey: string,
  currentStreak: number | null,
  level: string,
) {
  try {
    const templateContainer = document.createElement('div');

    const streakDisplay =
      currentStreak && currentStreak >= 1 ? `🔥 ${currentStreak}` : `🥶 0`;

    templateContainer.innerHTML = navBar.render({
      dashboardBaseUrl: window.HeyGoodGame.config.apiBaseUrl,
      gameKey,
      returnTo: encodeURIComponent(window.location.href),
      username: user.username,
      gameTitle,
      streakDisplay,
      level: formatLevel(level),
    });

    const streakDaysContainer = templateContainer.querySelector('#days');

    const streakDaysHTML = getLast7Days()
      .map((day, i) => {
        const isStreakDay = streakLogs.includes(day);
        const isCurrentDay = i === 6;
        return `<div class="day ${
          isStreakDay ? (isCurrentDay ? 'streakCurrentDay' : 'streak') : ''
        }">
                    ${day.split('-')[2]}
                  </div>`;
      })
      .join('');
    streakDaysContainer!.innerHTML = streakDaysHTML;

    const headerRightContainer = document.querySelector(
      'body > div > div.appShell > header > div.right',
    );
    headerRightContainer?.appendChild(templateContainer);

    const dropdownContent = templateContainer.querySelector('.dropdown-content');
    const userProfileButton = templateContainer.querySelector(
      '.user-profile-dropdown',
    );
    userProfileButton?.addEventListener('click', () => {
      dropdownContent?.classList.toggle('open');
    });

    // Close dropdown on outside click
    document.addEventListener('mousedown', (event) => {
      if (!templateContainer.contains(event.target as Node | null)) {
        dropdownContent?.classList.remove('open');
      }
    });
  } catch (error) {
    console.error('Error loading profile dropdown:', error);
  }
}

function updateSidebar(user: User, currentStreak: number | null) {
  const usernameElement = document.getElementById('name');
  const playStreak = document.querySelector('#current-play-streak');

  if (usernameElement) {
    usernameElement.textContent = user.name;
  }

  if (playStreak && currentStreak) {
    playStreak.textContent = currentStreak.toString();
  }
}

async function renderAccountMessage(
  gameTitle: string,
  svgSrc: string,
  gameKey: string,
  howToPlayURL: string,
) {
  try {
    const templateContainer = document.createElement('div');
    templateContainer.innerHTML = accountMessage.render({
      dashboardBaseUrl: window.HeyGoodGame.config.apiBaseUrl,
      gameKey,
      howToPlayURL,
      returnTo: encodeURIComponent(window.location.href),
    });

    templateContainer.querySelector('#account-logo')?.setAttribute('src', svgSrc);
    templateContainer.querySelector('#account-logo')?.setAttribute('alt', gameTitle);
    templateContainer.querySelector(
      '#game-title',
    )!.textContent = `NEW! ${gameTitle.toUpperCase()} ACCOUNTS`;

    const sidebar = document.getElementById('sidebar');
    const sidebarHeader = sidebar?.querySelector('.sidebar-header');
    const sidebarMenu = sidebar?.querySelector('.sidebar-menu');

    sidebar?.replaceChild(templateContainer, sidebarHeader!);
    sidebar?.replaceChild(templateContainer, sidebarMenu!);
  } catch (error) {
    console.error('Error loading account message:', error);
  }
}

async function initializeAppShell({
  gameKey,
  showBlogLink = false,
}: {
  gameKey: string;
  showBlogLink?: boolean;
}) {
  return new Promise((resolve, reject) => {
    try {
      document.addEventListener('DOMContentLoaded', () => {
        const heyggBody = document.createElement('div');
        heyggBody.classList.add('heygg-body');

        const appShellContainer = document.createElement('div');
        appShellContainer.classList.add('appShell');
        appShellContainer.innerHTML = appShell.render({
          dashboardBaseUrl: window.HeyGoodGame.config.apiBaseUrl,
          gameKey,
          returnTo: encodeURIComponent(window.location.href),
        });

        const mainContent = document.getElementById('main-content');

        if (!mainContent) {
          throw new Error('Element with ID #main-content not found');
        }

        const mainContentCloned = mainContent.cloneNode(true) as HTMLElement;

        document.body.replaceChildren(heyggBody);
        heyggBody.appendChild(appShellContainer);
        heyggBody.appendChild(mainContentCloned);

        heyggBody.insertAdjacentHTML('beforeend', footer.render({ showBlogLink }));

        resolve(true);
      });
    } catch (error) {
      reject(error);
    }
  });
}

function addScriptToHead(src: string, async = true, defer = true) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = async;
    script.defer = defer;
    document.head.appendChild(script);
    script.onload = () => {
      resolve(true);
    };
    script.onerror = () => {
      reject(new Error(`Failed to load script: ${src}`));
    };
  });
}

async function handleAuthAndSetupUI({
  gameKey,
  gameTitle,
  level,
  svgSrc,
  riveSrc,
  howToPlayURL,
  primaryColor,
  svgLogoSrc,
  showBlogLink = false,
  localStorageKeys,
  devToolbarEnabled,
}: HandleAuthAndSetupUIProps) {
  primaryColor &&
    document.documentElement.style.setProperty(
      '--heygg-primary-color',
      primaryColor,
    );

  await initializeAppShell({ gameKey, showBlogLink });

  const sidebar = document.getElementById('sidebar');
  const header = document.querySelector('body > div > div.appShell > header');
  const canvas = document.getElementById('canvas');
  const mainLogo = document.getElementById('main-logo');

  const today = new Date();
  const sevenDaysAgo = new Date();
  const since = new Date(sevenDaysAgo.setDate(today.getDate() - 7))
    .toISOString()
    .split('T')[0];

  mainLogo?.setAttribute('src', svgLogoSrc);
  mainLogo?.setAttribute('alt', `${gameTitle} Logo`);

  try {
    const { user } = await Auth().then(() => {
      const user = localStorage.getItem('heygg_user');

      return { user: user ? JSON.parse(user) : null };
    });

    if (user) {
      console.log('Logged in! : ', user);

      const [streakLogs = [], streaks = []] = await Promise.all([
        Streaks.getLogs(gameKey, level, since, 'win'),
        Streaks.getStreaks(gameKey, level),
      ]);

      const currentStreak = getCurrentStreak(streaks);

      const navbar = document.querySelector(
        'body > div.heygg-body > div.appShell > header > div.right > ul:nth-child(1)',
      ) as HTMLElement;

      navbar.remove();

      renderProfileDropdown(user, gameTitle, streakLogs, gameKey, currentStreak, level);
      updateSidebar(user, currentStreak);
    } else {
      console.log('Not logged in!');

      renderAccountMessage(gameTitle, svgSrc, gameKey, howToPlayURL);

      // Initialize Google One Tap if client ID is configured
      const googleClientId = window.HeyGoodGame?.config?.googleClientId;
      if (googleClientId) {
        try {
          await addScriptToHead('https://accounts.google.com/gsi/client');
          Google.initializeOneTap(googleClientId, {
            autoSelect: false,
            cancelOnTapOutside: true
          });
        } catch (error) {
          console.warn('Failed to load Google One Tap:', error);
        }
      }
    }
  } catch (error) {
    console.error('Error loading profile dropdown:', error);
  }

  await addScriptToHead('https://unpkg.com/@rive-app/canvas-lite', false, false);

  const rive = (window as any).rive;

  if (rive && canvas) {
    const layout = new rive.Layout({
      fit: rive.Fit.Contain,
      alignment: rive.Alignment.Center,
    });

    const riveInstance = new rive.Rive({
      src: riveSrc,
      canvas: canvas,
      autoplay: true,
      stateMachines: 'State Machine 1',
      layout,
      onLoad: () => {
        riveInstance.resizeDrawingSurfaceToCanvas();
      },
      isTouchScrollEnabled: true,
    });

    document.addEventListener('mousedown', (event) => {
      if (
        sidebar &&
        header &&
        !sidebar.contains(event.target as any) &&
        !header?.contains(event.target as any)
      ) {
        sidebar.classList.remove('sidebar-active');

        const inputs = riveInstance.stateMachineInputs('State Machine 1');
        const openInput = inputs?.find((i: { name: string }) => i.name === 'Open');

        if (openInput) {
          openInput.value = false;
        }
      }
    });
  } else {
    console.warn('Rive not available or canvas element not found');
  }

  // Initialize DevToolbar
  const isLocalhost = window.location.hostname === 'localhost';
  const shouldEnableDevToolbar = devToolbarEnabled ?? isLocalhost;
  
  DevToolbar.init({
    gameKey,
    enabled: shouldEnableDevToolbar,
    position: "bottom-center",
    localStorageKeys,
  });
}

const toggleSidebar = () => {
  const sidebarElement = document.getElementById('sidebar');
  if (sidebarElement) {
    sidebarElement.classList.toggle('sidebar-active');
  }
};

export { handleAuthAndSetupUI, toggleSidebar };
